import { useScrollReveal, getRevealClasses } from "@/hooks/useScrollReveal";
import useSEO from '@/hooks/useSEO';
import { rankedBrokers, categoryAwards, rankingStats, rankingFaqs, type RankedBroker } from "@/mocks/rankingsData";
import { brokers } from "@/data/brokers";
import InteractiveAnalytics from "@/pages/rankings/components/InteractiveAnalytics";
import { Link } from "react-router-dom";
import { useState, useEffect, useRef, useCallback } from "react";

/* ------------------------------------------------------------------ */
/*  Shared Helpers                                                     */
/* ------------------------------------------------------------------ */

function SectionHeader({ badge, badgeIcon, title, subtitle, badgeColor = "#8B5CF6" }: {
  badge: string; badgeIcon: string; title: string; subtitle?: string; badgeColor?: string;
}) {
  return (
    <div className="text-center mb-12 md:mb-16">
      <div
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-5"
        style={{ borderColor: `${badgeColor}/30`, backgroundColor: `${badgeColor}/8` }}
      >
        <i className={`${badgeIcon}`} style={{ color: badgeColor }}></i>
        <span className="text-sm text-[#F8FAFC] font-medium">{badge}</span>
      </div>
      <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#F8FAFC] mb-4">{title}</h2>
      {subtitle && <p className="text-base text-[#94A3B8] max-w-2xl mx-auto">{subtitle}</p>}
    </div>
  );
}

function ScoreToColor(score: number): string {
  if (score >= 9.0) return "#10B981";
  if (score >= 8.0) return "#8B5CF6";
  if (score >= 7.0) return "#F59E0B";
  return "#EF4444";
}

/* ------------------------------------------------------------------ */
/*  Module 1: Hero                                                     */
/* ------------------------------------------------------------------ */

function HeroSection() {
  return (
    <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden bg-[#0B0F19]">
      {/* Background ambient glows */}
      <div className="absolute top-0 left-1/3 w-[700px] h-[700px] bg-[#F59E0B]/6 rounded-full blur-[180px]" />
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[#8B5CF6]/5 rounded-full blur-[130px]" />

      <div className="relative max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center max-w-4xl mx-auto mb-10 md:mb-14">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/20 mb-6">
            <i className="ri-trophy-line text-[#F59E0B]"></i>
            <span className="text-xs font-bold text-[#F59E0B] uppercase tracking-wider">Q3 2026 Rankings</span>
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-[#F8FAFC] mb-6 tracking-tight leading-tight">
            Global Forex Broker{" "}
            <span className="text-gradient">Rankings</span>
          </h1>
          <p className="text-base md:text-lg text-[#94A3B8] max-w-2xl mx-auto leading-relaxed">
            Six trusted brokers ranked across 8 dimensions using real account data — not marketing brochures.
            Every score backed by live trading tests, verified execution speeds, and actual cost calculations.
            No affiliate bias. No pay-to-play. Just the numbers.
          </p>

          {/* Quick jump pills */}
          <div className="flex flex-wrap items-center justify-center gap-2 mt-8">
            {rankedBrokers.map((b) => (
              <a
                key={b.slug}
                href={b.slug}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#1F2937] border border-[#374151] hover:border-[#F59E0B]/50 text-xs text-[#94A3B8] hover:text-[#F8FAFC] transition-all whitespace-nowrap"
              >
                <img src={b.logo} alt={b.name} className="w-5 h-5 rounded-md object-cover" />
                #{b.rank} {b.name}
              </a>
            ))}
          </div>
        </div>

        {/* Stats Strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {rankingStats.map((stat) => (
            <div
              key={stat.label}
              className="bg-[#111827]/80 border border-[#1F2937] rounded-xl p-5 md:p-6 text-center hover:border-[#374151] transition-all"
            >
              <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center">
                <i className={`${stat.icon} text-lg text-[#F59E0B]`}></i>
              </div>
              <div className="text-2xl md:text-3xl font-black text-[#F8FAFC] mb-1">{stat.value}</div>
              <div className="text-xs text-[#64748B] leading-tight">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Module 2: Overall Leaderboard — Glass Cards + Click Expand         */
/* ------------------------------------------------------------------ */

function CountUpAnimated({ target, isActive, style }: { target: number; isActive: boolean; style?: React.CSSProperties }) {
  const [display, setDisplay] = useState(0);
  const resetKey = `${isActive}_${target}`;

  useEffect(() => {
    if (!isActive) { setDisplay(0); return; }

    let startTime: number | null = null;
    const duration = 1000;
    let raf: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(target * eased);
      if (progress < 1) raf = requestAnimationFrame(animate);
    };

    const t = setTimeout(() => { raf = requestAnimationFrame(animate); }, 200);
    return () => { clearTimeout(t); if (raf) cancelAnimationFrame(raf); };
  }, [resetKey]);

  return <span style={style}>{display.toFixed(1)}</span>;
}

function AnimatedBar({ target, color, delay = 0, resetSignal }: { target: number; color: string; delay?: number; resetSignal: string }) {
  const [width, setWidth] = useState(0);
  const [display, setDisplay] = useState(0);
  const trigger = `${resetSignal}_${target}`;

  useEffect(() => {
    const barTimeout = setTimeout(() => setWidth(target * 10), 80 + delay * 1000);

    let startTime: number | null = null;
    const duration = 1200;
    let raf: number;

    const animTimeout = setTimeout(() => {
      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setDisplay(target * eased);
        if (progress < 1) raf = requestAnimationFrame(animate);
      };
      raf = requestAnimationFrame(animate);
    }, 150 + delay * 1000);

    return () => {
      clearTimeout(barTimeout);
      clearTimeout(animTimeout);
      if (raf) cancelAnimationFrame(raf);
      setWidth(0);
      setDisplay(0);
    };
  }, [trigger]);

  return (
    <>
      <div className="flex-1 h-1.5 bg-[#1F2937] rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-1000 ease-out"
          style={{ width: `${width}%`, backgroundColor: color }}
        />
      </div>
      <span className="text-[10px] font-bold text-[#94A3B8] w-7 text-right">{display.toFixed(1)}</span>
    </>
  );
}

function OverallLeaderboard() {
  const { ref, isVisible } = useScrollReveal(0.1);
  const [sortKey, setSortKey] = useState<"overall" | "cost" | "execution" | "regulation" | "platforms" | "bonuses">("overall");
  const [expandedBroker, setExpandedBroker] = useState<string | null>(null);
  const [hoveredBroker, setHoveredBroker] = useState<string | null>(null);
  const [mousePositions, setMousePositions] = useState<Record<string, { x: number; y: number }>>({});
  const [countUpReady, setCountUpReady] = useState(false);

  useEffect(() => {
    if (isVisible) { const t = setTimeout(() => setCountUpReady(true), 150); return () => clearTimeout(t); }
    else setCountUpReady(false);
  }, [isVisible]);

  const dimLabels: { key: typeof sortKey; label: string; icon: string }[] = [
    { key: "overall", label: "Overall", icon: "ri-medal-line" },
    { key: "cost", label: "Cost", icon: "ri-money-dollar-circle-line" },
    { key: "execution", label: "Execution", icon: "ri-flashlight-line" },
    { key: "regulation", label: "Regulation", icon: "ri-shield-check-line" },
    { key: "platforms", label: "Platforms", icon: "ri-smartphone-line" },
    { key: "bonuses", label: "Bonuses", icon: "ri-gift-line" },
  ];

  const sorted = [...rankedBrokers].sort((a, b) => {
    if (sortKey === "overall") return b.overall - a.overall;
    return b.dimensions[sortKey] - a.dimensions[sortKey];
  });

  const getScore = (broker: RankedBroker): number => {
    if (sortKey === "overall") return broker.overall;
    return broker.dimensions[sortKey];
  };

  const allDims = [
    { key: "cost" as const, label: "Cost", color: "#10B981" },
    { key: "execution" as const, label: "Execution", color: "#8B5CF6" },
    { key: "regulation" as const, label: "Regulation", color: "#F59E0B" },
    { key: "platforms" as const, label: "Platforms", color: "#3B82F6" },
    { key: "bonuses" as const, label: "Bonuses", color: "#EC4899" },
    { key: "support" as const, label: "Support", color: "#14B8A6" },
    { key: "depositWithdrawal" as const, label: "Dep/WD", color: "#F97316" },
    { key: "education" as const, label: "Education", color: "#A855F7" },
  ];

  const rankColor = (rank: number) => {
    if (rank === 1) return "#F59E0B";
    if (rank === 2) return "#A8A8A8";
    if (rank === 3) return "#D97706";
    return "#475569";
  };

  const scoreColor = (score: number) => {
    if (score >= 9.0) return "#10B981";
    if (score >= 8.0) return "#8B5CF6";
    if (score >= 7.0) return "#F59E0B";
    return "#EF4444";
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>, brokerName: string) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePositions(prev => ({ ...prev, [brokerName]: { x: ((e.clientX - rect.left) / rect.width) * 100, y: ((e.clientY - rect.top) / rect.height) * 100 } }));
  };

  return (
    <section ref={ref} className={`py-20 md:py-28 bg-[#0B0F19] ${getRevealClasses(isVisible, "up")}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {isVisible && (
          <style>{`
            @keyframes cardEnter {
              from { opacity: 0; transform: translateY(40px); }
              to   { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        )}

        {/* Section header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F59E0B]/8 border border-[#F59E0B]/15 mb-4">
              <i className="ri-bar-chart-grouped-line text-[#F59E0B] text-xs"></i>
              <span className="text-[10px] font-bold text-[#F59E0B] uppercase tracking-widest">Leaderboard</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-[#F8FAFC] tracking-tight leading-tight">
              Broker Rankings<br />
              <span className="text-[#64748B] text-xl md:text-2xl font-bold">Q3 2026 · Live Account Verified</span>
            </h2>
          </div>

          {/* Sort pills — clean pill group */}
          <div className="flex items-center gap-0.5 p-1 bg-[#111827] border border-[#1F2937] rounded-xl md:rounded-full self-start lg:self-auto flex-wrap overflow-x-auto max-w-full">
            {dimLabels.map((d) => (
              <button
                key={d.key}
                onClick={() => setSortKey(d.key)}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[11px] font-semibold transition-all duration-300 whitespace-nowrap ${
                  sortKey === d.key
                    ? "bg-[#F8FAFC] text-[#0B0F19]"
                    : "text-[#64748B] hover:text-[#F8FAFC] hover:bg-[#1F2937]"
                }`}
              >
                <i className={`${d.icon} text-[10px]`}></i>
                {d.label}
              </button>
            ))}
          </div>
        </div>

        {/* Card Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {sorted.map((broker, idx) => {
            const score = getScore(broker);
            const isExpanded = expandedBroker === broker.name;
            const isHovered = hoveredBroker === broker.name;
            const staggerDelay = idx * 0.1;
            const mousePos = mousePositions[broker.name];

            return (
              <div
                key={broker.name}
                className="group relative rounded-2xl border transition-all duration-500 overflow-hidden cursor-pointer"
                style={{
                  backgroundColor: isHovered ? `${broker.color}06` : "#111827",
                  borderColor: isHovered ? `${broker.color}35` : "#1F2937",
                  transform: isHovered ? "translateY(-4px)" : "translateY(0)",
                  boxShadow: isHovered ? `0 16px 48px -12px ${broker.color}12` : "none",
                  animation: isVisible ? `cardEnter 0.55s cubic-bezier(0.16,1,0.3,1) ${staggerDelay}s both` : "none",
                  opacity: isVisible ? undefined : 0,
                }}
                onMouseMove={(e) => handleMouseMove(e, broker.name)}
                onMouseEnter={() => setHoveredBroker(broker.name)}
                onMouseLeave={() => { setHoveredBroker(null); setMousePositions(prev => { const n = { ...prev }; delete n[broker.name]; return n; }); }}
                onClick={() => setExpandedBroker(isExpanded ? null : broker.name)}
              >
                {/* Mouse-tracking radial glow */}
                {mousePos && (
                  <div
                    className="absolute inset-0 pointer-events-none transition-opacity duration-200"
                    style={{
                      opacity: isHovered ? 0.22 : 0,
                      background: `radial-gradient(circle 200px at ${mousePos.x}% ${mousePos.y}%, ${broker.color}28, transparent 70%)`,
                    }}
                  />
                )}

                {/* Top accent line */}
                <div
                  className="absolute top-0 left-0 right-0 h-[2px] transition-opacity duration-300"
                  style={{ backgroundColor: broker.color, opacity: isExpanded ? 1 : 0 }}
                />

                <div className="relative p-5 md:p-6">
                  {/* ── Header row ── */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 text-sm font-black"
                        style={{ backgroundColor: `${rankColor(broker.rank)}10`, color: rankColor(broker.rank), borderWidth: 1, borderStyle: "solid", borderColor: `${rankColor(broker.rank)}20` }}
                      >
                        #{broker.rank}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: `${broker.color}15` }}>
                            <img src={broker.logo} alt={broker.name} className="w-6 h-6 rounded object-cover" />
                          </div>
                          <h3 className="text-sm font-bold text-[#F8FAFC]">{broker.name}</h3>
                        </div>
                        <p className="text-[10px] text-[#64748B] mt-0.5 line-clamp-1 max-w-[180px]">{broker.tagline}</p>
                      </div>
                    </div>

                    {/* Score */}
                    <div className="text-right shrink-0 flex flex-col items-end">
                      <CountUpAnimated
                        target={score}
                        isActive={countUpReady}
                        style={{ fontSize: "1.75rem", fontWeight: 900, lineHeight: 1, color: scoreColor(score) }}
                      />
                      <span className="text-[9px] text-[#64748B]">/10</span>
                    </div>
                  </div>

                  {/* ── Compact dimension bars (always 4) ── */}
                  <div className="space-y-2 mb-3">
                    {allDims.slice(0, 4).map((d) => (
                      <div key={d.key} className="flex items-center gap-2.5">
                        <span className="text-[9px] font-semibold text-[#64748B] w-7 shrink-0">{d.label}</span>
                        <AnimatedBar
                          target={broker.dimensions[d.key]}
                          color={d.color}
                          delay={staggerDelay}
                          resetSignal={`${sortKey}_${isVisible}`}
                        />
                      </div>
                    ))}
                  </div>

                  {/* ── Expand teaser ── */}
                  <div className={`flex items-center justify-center gap-1.5 transition-all duration-300 ${isExpanded ? "mb-1" : "mb-0"}`}>
                    <span className="text-[9px] text-[#475569] font-medium">
                      {isExpanded ? "Collapse" : "All 8 dimensions"}
                    </span>
                    <i
                      className={`ri-arrow-down-s-line text-[10px] text-[#475569] transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`}></i>
                  </div>

                  {/* ── Expanded content ── */}
                  <div
                    className="grid transition-all duration-400 ease-out"
                    style={{
                      gridTemplateRows: isExpanded ? "1fr" : "0fr",
                    }}
                  >
                    <div className="overflow-hidden">
                      <div className="pt-2 space-y-2">
                        {allDims.slice(4).map((d) => (
                          <div key={d.key} className="flex items-center gap-2.5">
                            <span className="text-[9px] font-semibold text-[#64748B] w-7 shrink-0">{d.label}</span>
                            <AnimatedBar
                              target={broker.dimensions[d.key]}
                              color={d.color}
                              delay={0.05}
                              resetSignal={`${sortKey}_${isVisible}_exp`}
                            />
                          </div>
                        ))}
                      </div>

                      {/* Standout quote */}
                      <div className="mt-3 pt-3 border-t border-[#1F2937]">
                        <p className="text-[11px] text-[#94A3B8] leading-relaxed italic">
                          "{broker.standout.slice(0, 120)}..."
                        </p>
                      </div>

                      {/* CTA link */}
                      <Link
                        to={broker.slug}
                        className="mt-3 flex items-center justify-center gap-1.5 w-full py-2 rounded-lg text-[10px] font-bold transition-all whitespace-nowrap hover:brightness-110"
                        style={{ backgroundColor: `${broker.color}12`, color: broker.color }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {broker.name === "XM" ? "Full XM Review" : `XM vs ${broker.name}`}
                        <i className="ri-arrow-right-line text-[10px]"></i>
                      </Link>
                    </div>
                  </div>

                  {/* ── Best-for tags ── */}
                  <div className={`flex flex-wrap gap-1.5 transition-all duration-300 ${isExpanded ? "mt-3" : "mt-0"}`}>
                    {broker.bestFor.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="text-[9px] font-semibold px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: `${broker.color}08`, color: broker.color, borderWidth: 1, borderStyle: "solid", borderColor: `${broker.color}18` }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom insight */}
        <div className="mt-8 bg-[#111827] border border-[#1F2937] rounded-xl px-5 py-4 flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-lightbulb-line text-[#F59E0B]"></i>
          </div>
          <p className="text-xs md:text-sm text-[#94A3B8] leading-relaxed">
            Rankings reflect the <span className="text-[#F8FAFC] font-semibold">average trader profile</span> — your specific style might favor a different broker.
            Scalpers: prioritize <span className="text-[#10B981] font-semibold">Cost + Execution</span>. Swing traders: look at <span className="text-[#F59E0B] font-semibold">Regulation + Swaps</span>.
            Sort by any dimension above to find your personal winner. <span className="text-[#64748B]">Click any card to see all 8 dimensions.</span>
          </p>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Module 3: Category Awards                                          */
/* ------------------------------------------------------------------ */

function CategoryAwards() {
  const { ref, isVisible } = useScrollReveal(0.1);

  return (
    <section ref={ref} className={`py-20 md:py-28 bg-[#0B0F19] ${getRevealClasses(isVisible, "up")}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeader
          badge="Category Champions" badgeIcon="ri-award-line" badgeColor="#10B981"
          title="Best Broker for Every Trading Style"
          subtitle="The overall ranking tells one story — but your trading style deserves its own champion. Here are the category winners that matter for real traders."
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {categoryAwards.map((award) => (
            <div
              key={award.category}
              className="group bg-[#111827] border border-[#1F2937] rounded-xl p-5 hover:border-[#374151] transition-all duration-300 flex flex-col"
            >
              {/* Icon */}
              <div className="w-10 h-10 rounded-lg bg-[#10B981]/10 flex items-center justify-center">
                <i className={`${award.icon} text-lg text-[#10B981]`}></i>
              </div>

              {/* Category name */}
              <h3 className="text-sm font-bold text-[#F8FAFC] mt-3 leading-snug">{award.category}</h3>

              {/* Winner */}
              <div className="flex items-center gap-2.5 mt-3 mb-3">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${award.winnerColor}15`, borderColor: `${award.winnerColor}30`, borderWidth: 1, borderStyle: "solid" }}
                >
                  <img src={award.winnerLogo} alt={award.winner} className="w-6 h-6 rounded object-contain" />
                </div>
                <div>
                  <span className="text-[11px] font-bold text-[#F8FAFC]">{award.winner}</span>
                  <div className="flex items-center gap-1">
                    <i className="ri-trophy-line text-[9px] text-[#F59E0B]"></i>
                    <span className="text-[9px] text-[#F59E0B] font-semibold">WINNER</span>
                  </div>
                </div>
              </div>

              {/* Reason */}
              <p className="text-[11px] text-[#94A3B8] leading-relaxed mb-3 line-clamp-4">{award.reason}</p>

              {/* Honorable mention */}
              <div className="pt-3 border-t border-[#1F2937]">
                <span className="text-[9px] text-[#64748B]">
                  <i className="ri-medal-line text-[10px] mr-1"></i>
                  {award.honorableMention}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Module 4: Interactive Analytics — Cost Calculator + Charts         */
/* ------------------------------------------------------------------ */

/* Component extracted to src/pages/rankings/components/InteractiveAnalytics.tsx */

/* ------------------------------------------------------------------ */
/*  Module 5: Cost Analysis Table                                      */
/* ------------------------------------------------------------------ */

function CostAnalysis() {
  const { ref, isVisible } = useScrollReveal(0.1);
  const [activeBroker, setActiveBroker] = useState<string>(rankedBrokers[0].name);
  const [lots, setLots] = useState<number>(10);
  const [transitioning, setTransitioning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const broker = rankedBrokers.find((b) => b.name === activeBroker) ?? rankedBrokers[0];

  // -- Auto-rotation: cycle through brokers every 5 seconds --
  const autoRotateRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeBrokerRef = useRef(activeBroker);
  const pausedRef = useRef(false);
  const hoveringRef = useRef(false);

  useEffect(() => { activeBrokerRef.current = activeBroker; }, [activeBroker]);
  useEffect(() => { pausedRef.current = isPaused; }, [isPaused]);
  useEffect(() => { hoveringRef.current = isHovering; }, [isHovering]);

  const scheduleNextRotate = useCallback(() => {
    if (autoRotateRef.current) clearTimeout(autoRotateRef.current);
    if (pausedRef.current || hoveringRef.current) return;
    autoRotateRef.current = setTimeout(() => {
      const currentIndex = rankedBrokers.findIndex((b) => b.name === activeBrokerRef.current);
      const nextIndex = (currentIndex + 1) % rankedBrokers.length;
      const nextName = rankedBrokers[nextIndex].name;
      setTransitioning(true);
      setTimeout(() => {
        setActiveBroker(nextName);
        setTransitioning(false);
      }, 180);
      scheduleNextRotate();
    }, 5000);
  }, []);

  useEffect(() => {
    scheduleNextRotate();
    return () => {
      if (autoRotateRef.current) clearTimeout(autoRotateRef.current);
    };
  }, []);

  // Resume auto-rotation when unpaused and not hovering
  useEffect(() => {
    if (!isPaused && !isHovering) {
      scheduleNextRotate();
    }
  }, [isPaused, isHovering]);

  const handleBrokerSwitch = (name: string) => {
    if (name === activeBroker) return;
    setTransitioning(true);
    setTimeout(() => {
      setActiveBroker(name);
      setTransitioning(false);
    }, 180);
    // Reset auto-rotation timer when user manually clicks
    scheduleNextRotate();
  };

  const parseAllIn = (str: string): number | null => {
    const match = str.replace(/,/g, "").match(/[\d.]+/);
    return match ? parseFloat(match[0]) : null;
  };

  const getCostColor = (val: number | null) => {
    if (val === null) return "#94A3B8";
    if (val <= 7) return "#10B981";
    if (val <= 12) return "#F59E0B";
    return "#EF4444";
  };

  const costChampion = rankedBrokers.reduce(
    (best, b) => {
      const cheapest = b.costBreakdown.reduce((min, row) => {
        const v = parseAllIn(row.allInEstimate) ?? 999;
        return v < min ? v : min;
      }, 999);
      return cheapest < best.cost ? { name: b.name, cost: cheapest } : best;
    },
    { name: "", cost: 999 }
  );

  return (
    <section ref={ref} className={`py-20 md:py-28 bg-[#0B0F19] ${getRevealClasses(isVisible, "up")}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* Section header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#EF4444]/30 bg-[#EF4444]/8 mb-5">
            <i className="ri-calculator-line text-[#EF4444]"></i>
            <span className="text-sm text-[#F8FAFC] font-medium">True Trading Cost</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#F8FAFC] mb-4">
            What You Actually Pay Per Lot
          </h2>
          <p className="text-base text-[#94A3B8] max-w-2xl mx-auto">
            Marketing spreads lie. Here&apos;s the real all-in cost (spread + commission) per lot on EUR/USD — measured from live accounts, not demo data.
          </p>
        </div>

        {/* Main panel — sidebar + content */}
        <div className="flex flex-col lg:flex-row gap-4 md:gap-5">

          {/* ── Left sidebar: broker tabs ── */}
          <div
            className="lg:w-52 shrink-0 grid grid-cols-2 sm:grid-cols-3 lg:flex lg:flex-col gap-2"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            {rankedBrokers.map((b) => (
              <button
                key={b.name}
                onClick={() => handleBrokerSwitch(b.name)}
                className="flex items-center gap-2.5 px-3.5 py-3 rounded-xl border transition-all duration-250 text-left group cursor-pointer whitespace-nowrap"
                style={{
                  backgroundColor: activeBroker === b.name ? `${b.color}10` : "#111827",
                  borderColor: activeBroker === b.name ? `${b.color}40` : "#1F2937",
                  boxShadow: activeBroker === b.name ? `0 0 0 1px ${b.color}20` : "none",
                }}
              >
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 text-[10px] font-black"
                  style={{
                    backgroundColor: activeBroker === b.name ? `${b.color}20` : `${b.color}10`,
                    color: b.color,
                  }}
                >
                  <img src={b.logo} alt={b.name} className="w-6 h-6 rounded object-cover" />
                </div>
                <div className="min-w-0">
                  <div
                    className="text-xs font-bold leading-none"
                    style={{ color: activeBroker === b.name ? "#F8FAFC" : "#94A3B8" }}
                  >
                    {b.name}
                  </div>
                  <div className="text-[9px] text-[#475569] mt-0.5">Rank #{b.rank}</div>
                </div>
                {costChampion.name === b.name && (
                  <div className="ml-auto shrink-0">
                    <i className="ri-trophy-fill text-[#F59E0B] text-[10px]"></i>
                  </div>
                )}
              </button>
            ))}

            {/* Pause / Play toggle */}
            <button
              onClick={() => setIsPaused((p) => !p)}
              className="flex items-center justify-center gap-1.5 w-full py-2.5 rounded-lg text-[10px] font-semibold transition-all whitespace-nowrap cursor-pointer"
              style={{
                backgroundColor: isPaused ? "rgba(16,185,129,0.12)" : "rgba(100,116,139,0.10)",
                color: isPaused ? "#10B981" : "#64748B",
                borderWidth: 1,
                borderStyle: "solid",
                borderColor: isPaused ? "rgba(16,185,129,0.25)" : "#1F2937",
              }}
            >
              <i className={`${isPaused ? "ri-play-fill" : "ri-pause-fill"} text-[11px]`}></i>
              {isPaused ? "Resume" : "Pause"}
            </button>

            {/* Lots calculator */}
            <div className="hidden lg:block mt-3 bg-[#111827] border border-[#1F2937] rounded-xl p-4">
              <div className="text-[9px] font-bold text-[#64748B] uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <i className="ri-calculator-line text-[#EF4444]"></i>
                Lot Calculator
              </div>
              <div className="flex items-center gap-1.5 mb-2">
                <button
                  onClick={() => setLots((l) => Math.max(1, l - 5))}
                  className="w-7 h-7 rounded-lg bg-[#1F2937] border border-[#374151] flex items-center justify-center text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#374151] transition-all cursor-pointer"
                >
                  <i className="ri-subtract-line text-[11px]"></i>
                </button>
                <input
                  type="number"
                  min={1}
                  max={9999}
                  value={lots}
                  onChange={(e) => setLots(Math.max(1, parseInt(e.target.value) || 1))}
                  className="flex-1 h-7 bg-[#0B0F19] border border-[#374151] rounded-lg text-center text-xs font-bold text-[#F8FAFC] outline-none focus:border-[#EF4444]/50"
                />
                <button
                  onClick={() => setLots((l) => Math.min(9999, l + 5))}
                  className="w-7 h-7 rounded-lg bg-[#1F2937] border border-[#374151] flex items-center justify-center text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#374151] transition-all cursor-pointer"
                >
                  <i className="ri-add-line text-[11px]"></i>
                </button>
              </div>
              <div className="text-[9px] text-[#475569] text-center">lots of EUR/USD</div>
            </div>
          </div>

          {/* ── Right panel ── */}
          <div className="flex-1 min-w-0">
            <div
              className="transition-all duration-200"
              style={{ opacity: transitioning ? 0 : 1, transform: transitioning ? "translateY(6px)" : "translateY(0)" }}
            >
              {/* Broker header */}
              <div
                className="rounded-2xl border p-5 mb-4 flex flex-col lg:flex-row lg:flex-wrap lg:items-start gap-4"
                style={{ backgroundColor: `${broker.color}06`, borderColor: `${broker.color}20` }}
              >
                {/* Logo + main info */}
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 text-base font-black"
                    style={{ backgroundColor: `${broker.color}18`, color: broker.color }}
                  >
                    <img src={broker.logo} alt={broker.name} className="w-6 h-6 rounded object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="text-lg font-black text-[#F8FAFC]">{broker.name}</h3>
                      {costChampion.name === broker.name && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F59E0B]/15 border border-[#F59E0B]/25 text-[9px] font-bold text-[#F59E0B]">
                          <i className="ri-trophy-fill"></i> COST CHAMPION
                        </span>
                      )}
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold"
                        style={{ backgroundColor: `${broker.color}12`, color: broker.color }}
                      >
                        Cost Score: {broker.dimensions.cost.toFixed(1)}/10
                      </span>
                    </div>
                    <p className="text-xs text-[#64748B] leading-relaxed">{broker.tagline}</p>
                    <div className="flex flex-wrap gap-3 mt-2">
                      {broker.bestFor.slice(0, 3).map((tag) => (
                        <span key={tag} className="text-[10px] text-[#94A3B8] flex items-center gap-1">
                          <i className="ri-check-line text-[#10B981] text-[10px]"></i>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Regulation + Open Account */}
                <div className="shrink-0 lg:ml-auto lg:text-right flex flex-col gap-3">
                  <div>
                    <div className="text-[10px] text-[#64748B] mb-1">Regulation</div>
                    <div className="text-[11px] font-bold text-[#10B981] leading-snug lg:max-w-[130px] lg:text-right">{broker.keyStats.regulation}</div>
                  </div>
                  <a
                    href={brokers[broker.name.toLowerCase().replace(/ /g, '') as keyof typeof brokers]?.href || '#'}
                    target="_blank"
                    rel="nofollow noopener"
                    className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold transition-all whitespace-nowrap hover:brightness-110"
                    style={{ backgroundColor: `${broker.color}`, color: "#FFFFFF" }}
                  >
                    Open Account
                    <i className="ri-arrow-right-up-line text-xs"></i>
                  </a>
                </div>
              </div>

              {/* Account type cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                {broker.costBreakdown.map((row, i) => {
                  const costVal = parseAllIn(row.allInEstimate);
                  const costColor = getCostColor(costVal);
                  const totalCost = costVal !== null ? `$${(costVal * lots).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—";
                  const minCost = Math.min(...broker.costBreakdown.map((r) => parseAllIn(r.allInEstimate) ?? 999));
                  const isCheapest = broker.costBreakdown.length > 1 && costVal !== null && costVal === minCost;

                  return (
                    <div
                      key={i}
                      className="relative bg-[#111827] border rounded-xl p-4 flex flex-col gap-2 hover:border-opacity-60 transition-all duration-200"
                      style={{ borderColor: isCheapest ? `${costColor}35` : "#1F2937" }}
                    >
                      {isCheapest && (
                        <div
                          className="absolute top-2.5 right-2.5 text-[8px] font-bold px-1.5 py-0.5 rounded-full"
                          style={{ backgroundColor: `${costColor}15`, color: costColor }}
                        >
                          CHEAPEST
                        </div>
                      )}

                      {/* Account name */}
                      <div>
                        <div className="text-[10px] text-[#475569] uppercase tracking-wider font-semibold mb-0.5">Account</div>
                        <div className="text-sm font-bold text-[#F8FAFC]">{row.accountName}</div>
                      </div>

                      {/* Spread + Commission inline */}
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <div className="text-[9px] text-[#475569] uppercase tracking-wider mb-0.5">Spread</div>
                          <div className="text-[11px] font-semibold text-[#94A3B8]">{row.spread}</div>
                        </div>
                        <div>
                          <div className="text-[9px] text-[#475569] uppercase tracking-wider mb-0.5">Commission</div>
                          <div className="text-[11px] font-semibold text-[#94A3B8]">{row.commission}</div>
                        </div>
                      </div>

                      {/* Divider */}
                      <div className="border-t border-[#1F2937]"></div>

                      {/* All-in cost — big highlight */}
                      <div>
                        <div className="text-[9px] text-[#475569] uppercase tracking-wider mb-1">All-In / Lot</div>
                        <div className="text-2xl font-black leading-none" style={{ color: costColor }}>
                          {row.allInEstimate}
                        </div>
                      </div>

                      {/* Lot calculator result */}
                      <div
                        className="rounded-lg px-3 py-2 flex items-center justify-between"
                        style={{ backgroundColor: `${costColor}08`, borderWidth: 1, borderStyle: "solid", borderColor: `${costColor}15` }}
                      >
                        <span className="text-[9px] text-[#475569]">{lots} lots total</span>
                        <span className="text-sm font-black" style={{ color: costColor }}>{totalCost}</span>
                      </div>

                      {/* Min deposit */}
                      <div className="flex items-center justify-between pt-1">
                        <span className="text-[9px] text-[#475569]">Min deposit</span>
                        <span className="text-[10px] font-bold text-[#94A3B8]">{row.minDeposit}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Mobile lot calculator */}
              <div className="lg:hidden bg-[#111827] border border-[#1F2937] rounded-xl p-4 mb-4 flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg bg-[#EF4444]/10 flex items-center justify-center shrink-0">
                  <i className="ri-calculator-line text-[#EF4444] text-xs"></i>
                </div>
                <span className="text-xs text-[#94A3B8] shrink-0">Simulate</span>
                <div className="flex items-center gap-2 ml-auto">
                  <button
                    onClick={() => setLots((l) => Math.max(1, l - 5))}
                    className="w-7 h-7 rounded-lg bg-[#1F2937] flex items-center justify-center text-[#94A3B8] hover:text-[#F8FAFC] transition-all cursor-pointer"
                  >
                    <i className="ri-subtract-line text-[11px]"></i>
                  </button>
                  <input
                    type="number"
                    min={1}
                    max={9999}
                    value={lots}
                    onChange={(e) => setLots(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-14 h-7 bg-[#0B0F19] border border-[#374151] rounded-lg text-center text-xs font-bold text-[#F8FAFC] outline-none"
                  />
                  <button
                    onClick={() => setLots((l) => Math.min(9999, l + 5))}
                    className="w-7 h-7 rounded-lg bg-[#1F2937] flex items-center justify-center text-[#94A3B8] hover:text-[#F8FAFC] transition-all cursor-pointer"
                  >
                    <i className="ri-add-line text-[11px]"></i>
                  </button>
                  <span className="text-[10px] text-[#475569]">lots EUR/USD</span>
                </div>
              </div>

              {/* Cross-broker cost comparison bar */}
              <div className="bg-[#111827] border border-[#1F2937] rounded-xl p-4">
                <div className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider mb-4 flex items-center gap-1.5">
                  <i className="ri-bar-chart-horizontal-line text-[#EF4444] text-xs"></i>
                  Cheapest Account — All Brokers Compared
                </div>
                <div className="space-y-2.5">
                  {[...rankedBrokers]
                    .map((b) => {
                      const cheapest = b.costBreakdown.reduce((best, row) => {
                        const v = parseAllIn(row.allInEstimate) ?? 999;
                        return v < best.val ? { val: v, label: row.allInEstimate, account: row.accountName } : best;
                      }, { val: 999, label: "—", account: "" });
                      return { ...b, cheapestCost: cheapest.val, cheapestLabel: cheapest.label, cheapestAccount: cheapest.account };
                    })
                    .sort((a, b) => a.cheapestCost - b.cheapestCost)
                    .map((b, i) => {
                      const max = 20;
                      const pct = Math.min((b.cheapestCost / max) * 100, 100);
                      const barColor = getCostColor(b.cheapestCost);
                      const isActive = b.name === activeBroker;
                      return (
                        <button
                          key={b.name}
                          onClick={() => handleBrokerSwitch(b.name)}
                          className="w-full flex items-center gap-2.5 group cursor-pointer"
                        >
                          <div
                            className="w-5 h-5 rounded-md flex items-center justify-center text-[8px] font-black shrink-0"
                            style={{ backgroundColor: `${b.color}15`, color: b.color }}
                          >
                            <img src={b.logo} alt={b.name} className="w-6 h-6 rounded object-cover" />
                          </div>
                          <span
                            className="text-[10px] font-semibold w-14 text-left shrink-0"
                            style={{ color: isActive ? "#F8FAFC" : "#64748B" }}
                          >
                            {b.name}
                          </span>
                          <div className="flex-1 h-4 bg-[#1F2937] rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all duration-500 ease-out"
                              style={{
                                width: isVisible ? `${pct}%` : "0%",
                                backgroundColor: barColor,
                                transitionDelay: `${i * 0.08}s`,
                                opacity: isActive ? 1 : 0.6,
                              }}
                            />
                          </div>
                          <span
                            className="text-[10px] font-black w-16 text-right shrink-0"
                            style={{ color: barColor }}
                          >
                            {b.cheapestLabel}
                          </span>
                          <span className="text-[9px] text-[#475569] w-20 text-left shrink-0 hidden md:block">
                            ({b.cheapestAccount})
                          </span>
                          {i === 0 && (
                            <i className="ri-trophy-fill text-[#F59E0B] text-[10px] shrink-0"></i>
                          )}
                        </button>
                      );
                    })}
                </div>
                <p className="text-[10px] text-[#475569] mt-4 flex items-start gap-1.5">
                  <i className="ri-information-line text-xs shrink-0 mt-0.5"></i>
                  Shorter bar = lower cost = better. Click any row to drill into that broker&apos;s account details above.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Module 6: Broker Quick Cards                                       */
/* ------------------------------------------------------------------ */

const dimConfig = [
  { key: "cost" as const,              label: "Cost",       icon: "ri-money-dollar-circle-line", color: "#10B981" },
  { key: "execution" as const,         label: "Execution",  icon: "ri-flashlight-line",           color: "#8B5CF6" },
  { key: "regulation" as const,        label: "Regulation", icon: "ri-shield-check-line",         color: "#F59E0B" },
  { key: "platforms" as const,         label: "Platforms",  icon: "ri-smartphone-line",           color: "#3B82F6" },
  { key: "bonuses" as const,           label: "Bonuses",    icon: "ri-gift-line",                 color: "#EC4899" },
  { key: "support" as const,           label: "Support",    icon: "ri-headphone-line",            color: "#14B8A6" },
  { key: "depositWithdrawal" as const, label: "Dep/WD",     icon: "ri-bank-line",                 color: "#F97316" },
  { key: "education" as const,         label: "Education",  icon: "ri-book-open-line",            color: "#A855F7" },
];

function BrokerQuickCards() {
  const { ref, isVisible } = useScrollReveal(0.1);
  const [viewMode, setViewMode] = useState<"cards" | "table">("cards");
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [mousePos, setMousePos] = useState<Record<string, { x: number; y: number }>>({});
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>, name: string) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePos((prev) => ({ ...prev, [name]: { x: ((e.clientX - rect.left) / rect.width) * 100, y: ((e.clientY - rect.top) / rect.height) * 100 } }));
  };

  const rankLabel = (r: number) => {
    if (r === 1) return { text: "#1", color: "#F59E0B" };
    if (r === 2) return { text: "#2", color: "#A8A8A8" };
    if (r === 3) return { text: "#3", color: "#D97706" };
    return { text: `#${r}`, color: "#475569" };
  };

  return (
    <section ref={ref} className={`py-20 md:py-28 bg-[#111827] ${getRevealClasses(isVisible, "up")}`}>
      {isVisible && (
        <style>{`
          @keyframes brokerCardIn {
            from { opacity: 0; transform: translateY(32px) scale(0.97); }
            to   { opacity: 1; transform: translateY(0) scale(1); }
          }
          @keyframes verdictSlideUp {
            from { opacity: 0; transform: translateY(12px); }
            to   { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      )}

      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* ── Header + view toggle ── */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 mb-10 md:mb-14">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F59E0B]/8 border border-[#F59E0B]/15 mb-4">
              <i className="ri-eye-line text-[#F59E0B] text-xs"></i>
              <span className="text-[10px] font-bold text-[#F59E0B] uppercase tracking-widest">At a Glance</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-[#F8FAFC] tracking-tight leading-tight">
              Every Broker —<br />
              <span className="text-[#64748B] text-xl md:text-2xl font-bold">Quick Stats &amp; Quick Links</span>
            </h2>
          </div>
          {/* View mode toggle */}
          <div className="flex items-center gap-1 p-1 bg-[#0B0F19] border border-[#1F2937] rounded-xl self-start sm:self-auto shrink-0">
            <button
              onClick={() => setViewMode("cards")}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[11px] font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                viewMode === "cards" ? "bg-[#1F2937] text-[#F8FAFC]" : "text-[#64748B] hover:text-[#94A3B8]"
              }`}
            >
              <i className="ri-layout-grid-line text-[11px]"></i>
              Cards
            </button>
            <button
              onClick={() => setViewMode("table")}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[11px] font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                viewMode === "table" ? "bg-[#1F2937] text-[#F8FAFC]" : "text-[#64748B] hover:text-[#94A3B8]"
              }`}
            >
              <i className="ri-table-line text-[11px]"></i>
              Table
            </button>
          </div>
        </div>

        {/* ══════════════════ CARDS VIEW ══════════════════ */}
        {viewMode === "cards" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {rankedBrokers.map((broker, idx) => {
              const rl = rankLabel(broker.rank);
              const isHov = hoveredCard === broker.name;
              const mp = mousePos[broker.name];
              const staggerDelay = idx * 0.08;

              return (
                <div
                  key={broker.name}
                  className="group relative rounded-2xl border overflow-hidden transition-all duration-400 cursor-default flex flex-col"
                  style={{
                    backgroundColor: isHov ? `${broker.color}07` : "#1A2235",
                    borderColor: isHov ? `${broker.color}40` : "#2A3345",
                    transform: isHov ? "translateY(-5px)" : "translateY(0)",
                    animation: isVisible ? `brokerCardIn 0.5s cubic-bezier(0.16,1,0.3,1) ${staggerDelay}s both` : "none",
                    opacity: isVisible ? undefined : 0,
                  }}
                  onMouseMove={(e) => handleMouseMove(e, broker.name)}
                  onMouseEnter={() => setHoveredCard(broker.name)}
                  onMouseLeave={() => {
                    setHoveredCard(null);
                    setMousePos((p) => { const n = { ...p }; delete n[broker.name]; return n; });
                  }}
                >
                  {/* Mouse glow */}
                  {mp && (
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        opacity: isHov ? 0.18 : 0,
                        background: `radial-gradient(circle 220px at ${mp.x}% ${mp.y}%, ${broker.color}30, transparent 70%)`,
                        transition: "opacity 0.2s",
                      }}
                    />
                  )}

                  {/* Left color accent strip */}
                  <div
                    className="absolute left-0 top-0 bottom-0 w-[3px] transition-opacity duration-300"
                    style={{ backgroundColor: broker.color, opacity: isHov ? 1 : 0.25 }}
                  />

                  {/* ── Card header ── */}
                  <div className="relative pl-5 pr-5 pt-5 pb-4 border-b border-[#2A3345]/60 flex items-start gap-3">
                    {/* Rank badge */}
                    <div
                      className="absolute top-3 right-4 text-[11px] font-black px-2 py-0.5 rounded-full"
                      style={{ color: rl.color, backgroundColor: `${rl.color}12` }}
                    >
                      {rl.text}
                    </div>

                    {/* Logo circle */}
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 text-sm font-black"
                      style={{ backgroundColor: `${broker.color}18`, color: broker.color }}
                    >
                      <img src={broker.logo} alt={broker.name} className="w-6 h-6 rounded object-cover" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-black text-[#F8FAFC] leading-none mb-1">{broker.name}</h3>
                      <p className="text-[10px] text-[#64748B] leading-snug line-clamp-2 pr-8">{broker.tagline}</p>
                      {/* Overall score */}
                      <div className="flex items-baseline gap-1 mt-2">
                        <span className="text-2xl font-black leading-none" style={{ color: ScoreToColor(broker.overall) }}>
                          {broker.overall.toFixed(1)}
                        </span>
                        <span className="text-[10px] text-[#475569]">/10</span>
                        <span className="text-[9px] text-[#475569] ml-1">overall</span>
                      </div>
                    </div>
                  </div>

                  {/* ── Stats pills ── */}
                  <div className="relative pl-5 pr-5 pt-4 pb-3 grid grid-cols-2 gap-x-4 gap-y-3">
                    {([
                      { label: "Founded",    value: broker.keyStats.founded,     color: "#94A3B8" },
                      { label: "Min Deposit", value: broker.keyStats.minDeposit,  color: "#F8FAFC" },
                      { label: "Leverage",    value: broker.keyStats.maxLeverage, color: "#F59E0B" },
                      { label: "Instruments", value: broker.keyStats.instruments, color: "#94A3B8" },
                    ] as { label: string; value: string; color: string }[]).map((stat) => (
                      <div key={stat.label}>
                        <div className="text-[9px] text-[#475569] uppercase tracking-wider mb-0.5">{stat.label}</div>
                        <div className="text-[11px] font-bold" style={{ color: stat.color }}>{stat.value}</div>
                      </div>
                    ))}
                    <div className="col-span-2">
                      <div className="text-[9px] text-[#475569] uppercase tracking-wider mb-0.5">Regulation</div>
                      <div className="text-[11px] font-bold text-[#10B981] leading-snug">{broker.keyStats.regulation}</div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-[9px] text-[#475569] uppercase tracking-wider mb-0.5">Platforms</div>
                      <div className="text-[11px] font-bold text-[#94A3B8] leading-snug">{broker.keyStats.platforms}</div>
                    </div>
                  </div>

                  {/* ── Mini radar bars ── */}
                  <div className="relative pl-5 pr-5 pt-2 pb-4 border-t border-[#2A3345]/60">
                    <div className="text-[9px] text-[#475569] uppercase tracking-wider mb-2.5">Dimension Scores</div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                      {dimConfig.slice(0, 4).map((d) => (
                        <div key={d.key} className="flex items-center gap-1.5">
                          <span className="text-[8px] text-[#475569] w-10 shrink-0">{d.label}</span>
                          <div className="flex-1 h-1 bg-[#2A3345] rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all duration-700 ease-out"
                              style={{
                                width: isVisible ? `${broker.dimensions[d.key] * 10}%` : "0%",
                                backgroundColor: d.color,
                                transitionDelay: `${0.3 + staggerDelay + dimConfig.indexOf(d) * 0.05}s`,
                              }}
                            />
                          </div>
                          <span className="text-[8px] font-bold shrink-0" style={{ color: d.color }}>
                            {broker.dimensions[d.key].toFixed(1)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* ── Best-for tags ── */}
                  <div className="relative pl-5 pr-5 pb-4 flex flex-wrap gap-1.5">
                    {broker.bestFor.map((tag) => (
                      <span
                        key={tag}
                        className="text-[9px] font-semibold px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: `${broker.color}10`, color: broker.color, border: `1px solid ${broker.color}20` }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* ── Hover verdict overlay (PC only) ── */}
                  <div
                    className="hidden md:flex absolute inset-x-0 bottom-0 rounded-b-2xl px-5 pt-4 pb-5 flex-col gap-3 transition-all duration-300"
                    style={{
                      backgroundColor: `${broker.color}ee`,
                      transform: isHov ? "translateY(0)" : "translateY(101%)",
                      opacity: isHov ? 1 : 0,
                    }}
                  >
                    <p
                      className="text-[11px] leading-relaxed font-medium"
                      style={{ color: "rgba(0,0,0,0.75)", animation: isHov ? "verdictSlideUp 0.25s ease both" : "none" }}
                    >
                      {broker.standout.slice(0, 140)}…
                    </p>
                    <Link
                      to={broker.slug}
                      className="flex items-center justify-center gap-1.5 w-full py-2.5 rounded-xl text-xs font-black transition-all whitespace-nowrap bg-[#0B0F19]/70 text-[#F8FAFC] hover:bg-[#0B0F19]/90"
                      style={{ animation: isHov ? "verdictSlideUp 0.3s 0.05s ease both" : "none" }}
                    >
                      {broker.name === "XM" ? "Full XM Review" : `XM vs ${broker.name}`}
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>

                  {/* ── Mobile verdict (always visible, no overlay) ── */}
                  <div className="md:hidden relative px-5 pb-4 pt-3 border-t border-[#2A3345]/60 flex flex-col gap-2">
                    <p className="text-[11px] leading-relaxed text-[#94A3B8]">
                      {broker.standout.slice(0, 140)}…
                    </p>
                    <Link
                      to={broker.slug}
                      className="flex items-center justify-center gap-1.5 w-full py-2.5 rounded-xl text-xs font-black transition-all whitespace-nowrap"
                      style={{ backgroundColor: `${broker.color}18`, color: broker.color, border: `1px solid ${broker.color}30` }}
                    >
                      {broker.name === "XM" ? "Full XM Review" : `XM vs ${broker.name}`}
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>

                  {/* Spacer for PC hover overlay */}
                  <div className="hidden md:block h-[9.5rem]"></div>
                </div>
              );
            })}
          </div>
        )}

        {/* ══════════════════ TABLE VIEW ══════════════════ */}
        {viewMode === "table" && (
          <div className="-mx-4 md:mx-0 overflow-x-auto rounded-none md:rounded-2xl border-y md:border border-[#2A3345]">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="bg-[#0B0F19] border-b border-[#2A3345]">
                  <th className="text-left px-5 py-4 text-[10px] text-[#475569] uppercase tracking-wider font-semibold w-40">Broker</th>
                  <th className="text-center px-4 py-4 text-[10px] text-[#475569] uppercase tracking-wider font-semibold">Overall</th>
                  {dimConfig.map((d) => (
                    <th key={d.key} className="text-center px-3 py-4 text-[10px] text-[#475569] uppercase tracking-wider font-semibold">
                      <div className="flex flex-col items-center gap-1">
                        <i className={`${d.icon} text-xs`} style={{ color: d.color }}></i>
                        <span>{d.label}</span>
                      </div>
                    </th>
                  ))}
                  <th className="text-center px-4 py-4 text-[10px] text-[#475569] uppercase tracking-wider font-semibold">Details</th>
                </tr>
              </thead>
              <tbody>
                {rankedBrokers.map((broker, idx) => {
                  const isExp = expandedRow === broker.name;
                  const rl = rankLabel(broker.rank);
                  return (
                    <>
                      <tr
                        key={broker.name}
                        className="border-b border-[#2A3345]/60 transition-colors duration-150 cursor-pointer"
                        style={{ backgroundColor: isExp ? `${broker.color}08` : idx % 2 === 0 ? "#1A2235" : "#111827" }}
                        onClick={() => setExpandedRow(isExp ? null : broker.name)}
                      >
                        {/* Broker identity */}
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2.5">
                            <div
                              className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-[10px] font-black"
                              style={{ backgroundColor: `${broker.color}18`, color: broker.color }}
                            >
                              <img src={broker.logo} alt={broker.name} className="w-6 h-6 rounded object-cover" />
                            </div>
                            <div>
                              <div className="text-xs font-bold text-[#F8FAFC] leading-none">{broker.name}</div>
                              <div className="text-[9px] mt-0.5" style={{ color: rl.color }}>{rl.text} Overall</div>
                            </div>
                          </div>
                        </td>

                        {/* Overall score */}
                        <td className="px-4 py-4 text-center">
                          <span className="text-lg font-black" style={{ color: ScoreToColor(broker.overall) }}>
                            {broker.overall.toFixed(1)}
                          </span>
                        </td>

                        {/* Dimension scores */}
                        {dimConfig.map((d) => {
                          const score = broker.dimensions[d.key];
                          const opacity = 0.4 + (score / 10) * 0.6;
                          return (
                            <td key={d.key} className="px-3 py-4 text-center">
                              <div
                                className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-[11px] font-black"
                                style={{ backgroundColor: `${d.color}${Math.round(opacity * 15).toString(16).padStart(2, "0")}`, color: d.color }}
                              >
                                {score.toFixed(1)}
                              </div>
                            </td>
                          );
                        })}

                        {/* Expand toggle */}
                        <td className="px-4 py-4 text-center">
                          <div className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#2A3345] transition-all" style={{ backgroundColor: isExp ? `${broker.color}20` : undefined }}>
                            <i
                              className={`ri-arrow-down-s-line text-sm transition-transform duration-300 ${isExp ? "rotate-180" : ""}`}
                              style={{ color: isExp ? broker.color : "#475569" }}
                            ></i>
                          </div>
                        </td>
                      </tr>

                      {/* Expanded row */}
                      {isExp && (
                        <tr key={`${broker.name}-exp`} className="border-b border-[#2A3345]/60">
                          <td colSpan={11} className="px-5 py-5" style={{ backgroundColor: `${broker.color}05` }}>
                            <div className="flex flex-wrap gap-5 items-start">
                              {/* Key stats */}
                              <div className="flex flex-wrap gap-4">
                                {[
                                  { label: "Founded",     value: broker.keyStats.founded,      color: "#94A3B8" },
                                  { label: "Min Deposit", value: broker.keyStats.minDeposit,   color: "#F8FAFC" },
                                  { label: "Leverage",    value: broker.keyStats.maxLeverage,  color: "#F59E0B" },
                                  { label: "Instruments", value: broker.keyStats.instruments,  color: "#94A3B8" },
                                  { label: "Platforms",   value: broker.keyStats.platforms,    color: "#94A3B8" },
                                  { label: "Regulation",  value: broker.keyStats.regulation,   color: "#10B981" },
                                ].map((s) => (
                                  <div key={s.label} className="min-w-[90px]">
                                    <div className="text-[9px] text-[#475569] uppercase tracking-wider mb-0.5">{s.label}</div>
                                    <div className="text-[11px] font-bold" style={{ color: s.color }}>{s.value}</div>
                                  </div>
                                ))}
                              </div>

                              {/* Standout */}
                              <div className="flex-1 min-w-[240px]">
                                <div className="text-[9px] text-[#475569] uppercase tracking-wider mb-1.5">Standout Factor</div>
                                <p className="text-xs text-[#94A3B8] leading-relaxed">{broker.standout}</p>
                              </div>

                              {/* Best for tags + CTA */}
                              <div className="shrink-0">
                                <div className="text-[9px] text-[#475569] uppercase tracking-wider mb-2">Best For</div>
                                <div className="flex flex-col gap-1.5 mb-3">
                                  {broker.bestFor.map((tag) => (
                                    <span
                                      key={tag}
                                      className="text-[9px] font-semibold px-2 py-0.5 rounded-full w-fit"
                                      style={{ backgroundColor: `${broker.color}12`, color: broker.color, border: `1px solid ${broker.color}25` }}
                                    >
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                                <Link
                                  to={broker.slug}
                                  className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl text-[11px] font-black whitespace-nowrap transition-all hover:brightness-110"
                                  style={{ backgroundColor: `${broker.color}20`, color: broker.color, border: `1px solid ${broker.color}30` }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {broker.name === "XM" ? "Full XM Review" : `XM vs ${broker.name}`}
                                  <i className="ri-arrow-right-line"></i>
                                </Link>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Bottom note ── */}
        <div className="mt-8 flex flex-wrap items-center gap-5 justify-center">
          {["Real Money Tested", "Updated Q3 2026", "Zero Affiliate Bias", "6 Brokers · 8 Dimensions"].map((txt) => (
            <div key={txt} className="flex items-center gap-2 text-[11px] text-[#475569]">
              <div className="w-1.5 h-1.5 rounded-full bg-[#F59E0B]"></div>
              {txt}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Module 7: Methodology                                              */
/* ------------------------------------------------------------------ */

function MethodologySection() {
  const { ref, isVisible } = useScrollReveal(0.1);

  const steps = [
    {
      step: "01",
      /* @replace:rankings-title */

      title: "Forex Platform Rankings 2026 | Compare Global Broker Platforms",
      icon: "ri-test-tube-line",
      /* @replace:rankings-description */

      description: "Explore global forex platform rankings based on trading performance, execution quality, platform features, costs, and multiple evaluation dimensions. Compare leading brokers through structured data analysis.",
    },
    {
      step: "02",
      title: "Weighted Multi-Dimension Scoring",
      icon: "ri-scales-line",
      description: "Each broker is scored 0-10 across 8 dimensions. The overall score uses a weighted formula: Trading Cost (20%), Regulation & Security (20%), and Execution Speed (20%) carry the most weight because they directly impact your P&L. Platforms (10%), Bonuses (10%), Support (10%), Deposit/Withdrawal (5%), and Education (5%) complete the picture.",
    },
    {
      step: "03",
      title: "Community Cross-Validation",
      icon: "ri-user-voice-line",
      description: "We aggregate and verify real user reviews from Trustpilot, ForexPeaceArmy, and active trading forums. Every claim — positive or negative — is cross-referenced against our own live account testing before appearing in these rankings. If the community says something we can't verify, we don't include it.",
    },
    {
      step: "04",
      title: "Quarterly Re-Testing",
      icon: "ri-refresh-line",
      description: "Broker conditions change constantly. Spreads widen during volatility. Bonus programs expire. Regulatory statuses shift. We re-test every broker every quarter with fresh live accounts and update all scores for the new quarter. This Q3 2026 edition reflects testing conducted in July 2026.",
    },
  ];

  return (
    <section ref={ref} className={`py-20 md:py-28 bg-[#0B0F19] ${getRevealClasses(isVisible, "up")}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeader
          badge="How We Score" badgeIcon="ri-magic-line" badgeColor="#10B981"
          title="Our Ranking Methodology"
          subtitle="We don't copy-paste broker marketing pages. Here's exactly how every score is calculated — transparent and reproducible."
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {steps.map((step) => (
            <div
              key={step.step}
              className="group bg-[#111827] border border-[#1F2937] rounded-xl p-6 md:p-7 hover:border-[#374151] transition-all duration-300"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#10B981]/10 flex items-center justify-center shrink-0 group-hover:bg-[#10B981]/20 transition-colors">
                  <span className="text-[#10B981] font-black text-sm">{step.step}</span>
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#F8FAFC] mb-2">{step.title}</h3>
                  <p className="text-sm text-[#94A3B8] leading-relaxed">{step.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Trust badges */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-6 md:gap-10">
          {[
            "Real Money Tested",
            "Updated Quarterly",
            "Zero Affiliate Bias",
            "Community Verified",
          ].map((badge) => (
            <div key={badge} className="flex items-center gap-2 text-xs text-[#64748B]">
              <div className="w-2 h-2 rounded-full bg-[#10B981]"></div>
              {badge}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Module 8: FAQ                                                      */
/* ------------------------------------------------------------------ */

function FaqSection() {
  const { ref, isVisible } = useScrollReveal(0.1);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section ref={ref} className={`py-20 md:py-28 bg-[#111827] ${getRevealClasses(isVisible, "up")}`}>
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <SectionHeader
          badge="FAQ" badgeIcon="ri-question-answer-line" badgeColor="#F59E0B"
          title="Common Questions About Our Rankings"
          subtitle="Everything you need to know about how we rank, why certain brokers place where they do, and how to use these rankings."
        />

        <div className="space-y-3">
          {rankingFaqs.map((item, i) => (
            <div
              key={i}
              className="bg-[#1A2235] border border-[#2A3345] rounded-xl overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between p-5 text-left hover:bg-[#111827]/50 transition-colors"
              >
                <span className="text-sm font-semibold text-[#F8FAFC] pr-4">{item.q}</span>
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${
                    openIndex === i ? "bg-[#F59E0B] rotate-45" : "bg-[#1F2937]"
                  }`}
                >
                  <i className={`${openIndex === i ? "ri-close-line text-[#111827]" : "ri-add-line text-[#64748B]"} text-sm`}></i>
                </div>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === i ? "max-h-[400px] pb-5 px-5" : "max-h-0"
                }`}
              >
                <p className="text-sm text-[#94A3B8] leading-relaxed">{item.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Full Page                                                          */
/* ------------------------------------------------------------------ */

export default function Rankings() {
  useSEO({
    title: 'Forex Platform Rankings 2026 | Compare Global Broker Platforms',
    description: 'Explore global forex platform rankings based on trading performance, execution quality, platform features, costs, and multiple evaluation dimensions. Compare leading brokers through structured data analysis.',
    /* @replace:rankings-keywords */

    keywords: 'forex platform rankings, forex broker rankings, trading platform comparison, broker platform analysis, global forex brokers, forex trading platforms',
    /* @replace:rankings-canonical */

    canonical: '/rankings',
  });

  return (
    <div className="min-h-screen bg-[#111827]">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Global Forex Broker Rankings Q3 2026 — 8-Dimension Leaderboard & Category Awards",
        "description": "Comprehensive forex broker rankings: Tickmill, FxPro, IC Markets, XM, Axi, and FXTM scored across 8 dimensions using live account data. Category awards, cost analysis, and transparent methodology.",
        "keywords": "forex broker rankings,best forex broker 2026,Tickmill ranking,FxPro ranking,IC Markets ranking,XM ranking,broker comparison leaderboard",
      }) }} />

      <HeroSection />
      <OverallLeaderboard />
      <CategoryAwards />
      <InteractiveAnalytics />
      <CostAnalysis />
      <BrokerQuickCards />
      <MethodologySection />
      <FaqSection />
    </div>
  );
}